// Auth API — login (sets the shared vf_session cookie), signup, logout, me.
import { http } from "./http";
import type {
  ForgotResponse,
  LoginPayload,
  LoginResponse,
  OkResponse,
  PasswordResetPayload,
  SignupPayload,
  SignupResponse,
  Trainer,
  TrainerForgotPayload,
  TrainerResetResponse,
} from "./types";

export const authApi = {
  login: (body: LoginPayload) => http.post<LoginResponse>("/auth/login", body),
  /**
   * Wave 8 — request a trainer password reset by email (PUBLIC, no auth).
   * NON-ENUMERATING: the server always answers an identical generic success
   * whether or not the email matches an account; a `429` means the per-IP
   * request ceiling was crossed.
   */
  forgot: (body: TrainerForgotPayload) =>
    http.post<ForgotResponse>("/auth/forgot", body),
  /**
   * Wave 8 — consume a trainer reset token and set a new password (PUBLIC).
   * On success the server signs the trainer in (shared vf_session cookie) and
   * returns the freshly-authenticated trainer; a `400` means an invalid /
   * expired / already-used token, a `422` a too-short password (< 10 chars).
   */
  reset: (body: PasswordResetPayload) =>
    http.post<TrainerResetResponse>("/auth/reset", body),
  /**
   * Register a new trainer (expanded signup) and start a session. The server
   * creates the account, sets the shared `vf_session` cookie (so the new trainer
   * is logged straight in), and — when an email was given — mints + emails a
   * Wave-6 verification link. The phone is captured (E.164) but NOT SMS-verified
   * this wave. `409` when the username/handle is taken, `422` on a weak password
   * or a malformed/reserved handle.
   */
  signup: (body: SignupPayload) =>
    http.post<SignupResponse>("/auth/signup", body),
  logout: () => http.post<OkResponse>("/auth/logout"),
  /** Current trainer; throws ApiError(401) when unauthenticated. */
  me: (signal?: AbortSignal) => http.get<Trainer>("/auth/me", undefined, signal),
};
